import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Copy, Check, UserPlus, Users, Shield, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/Avatar'

const ROLES = [
  { value: 'member', label: 'Member', desc: 'Can view and edit tasks' },
  { value: 'viewer', label: 'Viewer', desc: 'Can view only' },
]

const PERM_LABELS = [
  { key: 'create_tasks', label: 'Create tasks' },
  { key: 'edit_tasks', label: 'Edit tasks' },
  { key: 'delete_tasks', label: 'Delete tasks' },
  { key: 'manage_sections', label: 'Manage sections' },
  { key: 'invite_members', label: 'Invite members' },
  { key: 'approve_tasks', label: 'Approve tasks' },
]

function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      className={cn(
        'w-9 h-5 rounded-full flex-shrink-0 relative transition-colors duration-200',
        on ? 'bg-accent' : 'bg-surface border border-border'
      )}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm"
        style={{
          transform: on ? 'translateX(18px)' : 'translateX(2px)',
          transition: 'transform 200ms cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      />
    </button>
  )
}

export function InviteModal({ projectId, onClose }) {
  const { inviteToProject, getProjectMembers, updateMemberPermissions, removeMember, projects, currentUser } = useAppStore()
  const project = projects.find((p) => p.id === projectId)
  const isOwner = project?.ownerId === currentUser?.id || project?.role === 'owner'

  const [email, setEmail] = useState('')
  const [role, setRole] = useState('member')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [inviteLink, setInviteLink] = useState(null)
  const [copied, setCopied] = useState(false)
  const [members, setMembers] = useState([])
  const [tab, setTab] = useState('invite')
  const [savingPerms, setSavingPerms] = useState({}) // userId → true while saving
  const [confirmRemove, setConfirmRemove] = useState(null) // userId pending confirmation

  const loadMembers = useCallback(async () => {
    const data = await getProjectMembers(projectId)
    setMembers(data)
  }, [projectId, getProjectMembers])

  useEffect(() => { loadMembers() }, [loadMembers])

  async function handleInvite(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { error, inviteLink: link } = await inviteToProject(projectId, email, role)
      if (error) { setError(error.message); return }
      setInviteLink(link)
      setEmail('')
    } catch (err) {
      console.error('inviteToProject error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  function copyLink() {
    navigator.clipboard.writeText(inviteLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handlePermChange(member, key, val) {
    const newPerms = { ...member.permissions, [key]: val }
    // Optimistic update
    setMembers((prev) => prev.map((m) => m.userId === member.userId ? { ...m, permissions: newPerms } : m))
    setSavingPerms((s) => ({ ...s, [member.userId]: true }))
    await updateMemberPermissions(projectId, member.userId, newPerms)
    setSavingPerms((s) => ({ ...s, [member.userId]: false }))
  }

  async function handleRemove(member) {
    if (confirmRemove !== member.userId) {
      setConfirmRemove(member.userId)
      setTimeout(() => setConfirmRemove(null), 3000) // auto-cancel after 3s
      return
    }
    setConfirmRemove(null)
    setMembers((prev) => prev.filter((m) => m.userId !== member.userId))
    await removeMember(projectId, member.userId)
  }

  const tabs = [
    { id: 'invite', label: 'Invite' },
    { id: 'members', label: `Members (${members.length})` },
    ...(isOwner ? [{ id: 'permissions', label: 'Permissions' }] : []),
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="bg-surface border border-border rounded-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: (project?.color || '#e63946') + '20', color: project?.color || '#e63946' }}>
            <UserPlus size={14} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-text">Invite to {project?.name}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg text-text-faint hover:text-text hover:bg-surface-raised transition-colors active:scale-[0.97]">
            <X size={14} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-5">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-1 py-3 mr-5 text-sm border-b-2 transition-colors ${tab === t.id ? 'border-accent text-text font-medium' : 'border-transparent text-text-muted hover:text-text'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          <AnimatePresence mode="wait">

            {/* ── Invite tab ── */}
            {tab === 'invite' && (
              <motion.div key="invite" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                <form onSubmit={handleInvite} className="flex flex-col gap-4">
                  <div>
                    <label className="text-xs text-text-muted mb-1.5 block">Email address</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.co" required autoFocus />
                  </div>
                  <div>
                    <label className="text-xs text-text-muted mb-1.5 block">Role</label>
                    <div className="flex gap-2">
                      {ROLES.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          onClick={() => setRole(r.value)}
                          className={`flex-1 text-left px-3 py-2.5 rounded-xl border text-sm transition-all active:scale-[0.98] ${role === r.value ? 'border-accent/40 bg-accent/8 text-text' : 'border-border text-text-muted hover:border-border-hover hover:text-text'}`}
                        >
                          <p className="font-medium text-xs">{r.label}</p>
                          <p className="text-[11px] text-text-faint mt-0.5">{r.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  {error && <p className="text-xs text-red-400">{error}</p>}
                  <Button type="submit" variant="primary" disabled={loading} className="w-full">
                    {loading ? 'Sending…' : 'Send invite'}
                  </Button>
                </form>

                <AnimatePresence>
                  {inviteLink && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-4 p-3 bg-surface-raised rounded-xl border border-border"
                    >
                      <p className="text-xs font-medium text-text mb-1">Invite sent ✓</p>
                      <p className="text-xs text-text-muted mb-2">They'll get an email with the link. You can also share it directly:</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-text font-mono flex-1 truncate">{inviteLink}</p>
                        <button
                          onClick={copyLink}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-accent/10 text-accent text-xs font-medium hover:bg-accent/20 transition-colors active:scale-[0.97]"
                        >
                          {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* ── Members tab ── */}
            {tab === 'members' && (
              <motion.div key="members" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                {members.length === 0 ? (
                  <div className="text-center py-6">
                    <Users size={20} className="text-text-faint mx-auto mb-2" />
                    <p className="text-sm text-text-muted">No members yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {members.map((m) => (
                      <div key={m.userId} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-surface-raised group">
                        <Avatar user={m} size="md" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text truncate">{m.name}</p>
                          <p className="text-xs text-text-faint truncate">{m.email}</p>
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-faint px-2 py-0.5 rounded-full bg-surface border border-border">
                          {m.role}
                        </span>
                        {isOwner && m.userId !== currentUser?.id && (
                          <button
                            onClick={() => handleRemove(m)}
                            style={{ opacity: confirmRemove === m.userId ? 1 : undefined }}
                            className={`opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-lg transition-all active:scale-[0.97] ${
                              confirmRemove === m.userId
                                ? 'h-7 px-2 gap-1 text-[10px] font-semibold text-red-400 bg-red-400/15 border border-red-400/30'
                                : 'w-7 h-7 text-text-faint hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            title={confirmRemove === m.userId ? 'Click again to confirm' : 'Remove member'}
                          >
                            {confirmRemove === m.userId ? 'confirm?' : <Trash2 size={13} />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* ── Permissions tab (owner only) ── */}
            {tab === 'permissions' && isOwner && (
              <motion.div key="permissions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                {members.filter((m) => m.role !== 'owner').length === 0 ? (
                  <div className="text-center py-6">
                    <Shield size={20} className="text-text-faint mx-auto mb-2" />
                    <p className="text-sm text-text-muted">No members to configure</p>
                    <p className="text-xs text-text-faint mt-1">Invite someone first</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {members.filter((m) => m.role !== 'owner').map((member) => (
                      <div key={member.userId}>
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-6 h-6 rounded-md bg-accent/15 flex items-center justify-center text-accent text-[10px] font-bold flex-shrink-0">
                            {member.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text truncate">{member.name}</p>
                            <p className="text-[11px] text-text-faint truncate">{member.email}</p>
                          </div>
                          {savingPerms[member.userId] && (
                            <span className="text-[10px] text-text-faint">Saving…</span>
                          )}
                        </div>
                        <div className="space-y-2.5 pl-8">
                          {PERM_LABELS.map(({ key, label }) => (
                            <div key={key} className="flex items-center justify-between gap-4">
                              <span className="text-xs text-text-muted">{label}</span>
                              <Toggle
                                on={member.permissions?.[key] !== false}
                                onChange={(val) => handlePermChange(member, key, val)}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 border-t border-border/50" />
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  )
}
